import { selectorFamily, type RecoilValue } from "recoil";

import { ComputedDataKey } from "../../constants/common.const";
import { RideComputedDataKeys } from "../../constants/rides.const";
import type { IExtendableDto, IRideTableDto } from "../../dto";
import {
  filterLiveMapRowsByHiddenDelayStatus,
  filterLiveMapRowsByRideFilter,
  flattenGroupedTableRows,
  getNearbyRidesIdsValues,
  getRidesIdsWithNearby,
  getTableFilteredGroupedRowsFromMap,
  guardIsRecoilDefaultValue,
  isEntityServerSidePaginated,
  isTableSelectionAtLimit,
  isValidPositiveNumber,
  rejectTableSelectionOverLimit,
  resolveDisplayedRowsCount,
  resolveSelectedTableRowValues,
  sessionStorageHelpers,
  unique,
  warnTableSelectionLimitReached,
} from "../../helpers";
import type { DtoFieldType, INotificationDto, RydeUiUnit } from "../../models";
import {
  highlightedAfterEditAtomFamily,
  liveMapGridToolbarStatsAtomFamily,
  liveMapHiddenDelayStatusesAtomFamily,
  liveMapRideFilterAtomFamily,
  nearByFilterAtomFamily,
  nearbyFilteringRideIdAtom,
  nearbyOtherRidesIdsBatchAtom,
  notificationsImportantAtom,
  selectedTableRowsIdsAtomFamily,
  selectedTableRowsIncrementAtomFamily,
  showOnlyRidesWithNewIncidentsAtomFamily,
  showSelectedOnlyRowsAtomFamily,
  starredTableRowsIdsAtomFamily,
  tableRowsAtomFamily,
  tableRowsCountAtomFamily,
} from "../atoms";
import { boundedSelector, boundedSelectorFamily } from "../selector-factories";
import {
  tableFilteredAllGroupsRowsIdsSF,
  tableFilteredGroupedRowsMapSF,
} from "./table-layout.selectors";

export const isSelectedIdSF = selectorFamily<
  boolean,
  [entity: RydeUiUnit, id: number]
>({
  key: "isSelectedRowSF",
  get:
    ([entity, id]) =>
    ({ get }) => {
      const selectedIdsSet = get(selectedTableRowsIdsSetSF(entity));
      return selectedIdsSet.has(id);
    },
  set:
    ([entity, id]) =>
    ({ set, get }, isSelected) => {
      if (isSelected) {
        const selectedIds = get(selectedTableRowsIdsAtomFamily(entity));

        if (
          !selectedIds.includes(id) &&
          isTableSelectionAtLimit(selectedIds, entity)
        ) {
          warnTableSelectionLimitReached(entity);
          return;
        }

        set(selectedTableRowsIdsAtomFamily(entity), selIds =>
          unique([...selIds, id]),
        );
        set(selectedTableRowsIncrementAtomFamily(entity), old => old + 1);
      } else {
        set(selectedTableRowsIdsAtomFamily(entity), selectedIds => {
          const filtered = selectedIds?.filter(selId => selId !== id);

          if (filtered.length === 0) {
            set(selectedTableRowsIncrementAtomFamily(entity), () => 0);
          }

          return filtered;
        });
      }
    },
});

export const selectedIdsSF = selectorFamily<number[], RydeUiUnit>({
  key: "selectedIdsSF",
  get:
    entity =>
    ({ get }) => {
      return get(selectedTableRowsIdsAtomFamily(entity));
    },
  set:
    entity =>
    ({ set }, selectedIds) => {
      if (guardIsRecoilDefaultValue(selectedIds)) {
        set(selectedTableRowsIdsAtomFamily(entity), selectedIds);

        return;
      }

      set(selectedTableRowsIdsAtomFamily(entity), prev => {
        const resolved =
          typeof selectedIds === "function"
            ? (selectedIds as (value: number[]) => number[])(prev)
            : selectedIds;

        const nextSelectedIds = rejectTableSelectionOverLimit(resolved, entity);

        return nextSelectedIds ?? prev;
      });
    },
});

export const selectedTableRowsIdsSetSF = selectorFamily<
  Set<number>,
  RydeUiUnit
>({
  key: "selectedTableRowsIdsSetSF",
  get:
    entity =>
    ({ get }) => {
      const selectedIds = get(selectedTableRowsIdsAtomFamily(entity));
      return new Set(selectedIds);
    },
});

export const tableGroupIdsSF = boundedSelectorFamily<
  number[],
  [
    entity: RydeUiUnit,
    groupField: string | number | undefined,
    groupValue: DtoFieldType,
  ]
>({
  key: "tableGroupIdsSF",
  get:
    ([entity, groupField, groupValue]) =>
    ({ get }) => {
      const groupedRowsMap = get(tableFilteredGroupedRowsMapSF(entity));
      const rows = getTableFilteredGroupedRowsFromMap({
        groupedRowsMap,
        groupingFieldValue: groupValue,
        groupingFieldNameResolved: groupField,
      });

      return rows.map(({ id }) => id);
    },
});

export const isTableGroupIdsSelectedSF = boundedSelectorFamily<
  boolean,
  [
    entity: RydeUiUnit,
    groupField: string | number | undefined,
    groupValue: DtoFieldType,
  ]
>({
  key: "isTableGroupIdsSelectedSF",
  get:
    ([entity, groupField, groupValue]) =>
    ({ get }) => {
      const ids = get(tableGroupIdsSF([entity, groupField, groupValue]));

      if (!ids?.length) {
        return false;
      }

      const selectedIdsSet = get(selectedTableRowsIdsSetSF(entity));
      return ids.every(id => selectedIdsSet.has(id));
    },
});

export const isStarredTableRowIdSF = selectorFamily<
  boolean,
  [entity: RydeUiUnit, id: number]
>({
  key: "isStarredRideRowIdSF",
  get:
    ([entity, id]) =>
    ({ get }) => {
      const starredIds = get(starredTableRowsIdsAtomFamily(entity));
      const starredIdsSet = new Set(starredIds);
      return starredIdsSet.has(id);
    },
  set:
    ([entity, id]) =>
    ({ set, get }, isSelected) => {
      const prev = get(starredTableRowsIdsAtomFamily(entity));
      let updated: number[];

      if (isSelected) {
        const prevSet = new Set(prev);
        updated = prevSet.has(id) ? prev : [...prev, id];
      } else {
        updated = prev.filter(selId => selId !== id);
      }

      set(starredTableRowsIdsAtomFamily(entity), updated);
      sessionStorageHelpers.setItem(`starred_ids_${entity}`, updated);
    },
});

export const starredRowsCountSF = selectorFamily<number, RydeUiUnit>({
  key: "starredRowsCountSF",
  get:
    entity =>
    ({ get }) => {
      const rows = get(starredTableRowsIdsAtomFamily(entity));
      return rows?.length || 0;
    },
});

export const tableRowsCountSF = boundedSelectorFamily<number, RydeUiUnit>({
  key: "tableRowsCountSF",
  get:
    entity =>
    ({ get }) => {
      const rows = get(tableRowsAtomFamily(entity));
      return rows?.length || 0;
    },
});

export const displayedRowsCountSF = boundedSelectorFamily<number, RydeUiUnit>({
  key: "displayedRowsCountSF",
  get:
    entity =>
    ({ get }) =>
      resolveDisplayedRowsCount({
        entity,
        shouldShowSelectedOnlyRows: get(showSelectedOnlyRowsAtomFamily(entity)),
        getServerRowsCount: () => get(tableRowsCountAtomFamily(entity)),
        getLoadedRowsCount: () =>
          get(tableFilteredAllGroupsRowsIdsSF(entity)).size,
      }),
});

export const tableRowsComputedSF = boundedSelectorFamily<
  IExtendableDto[] | undefined,
  RydeUiUnit
>({
  key: "tableRowsComputedSF",
  get:
    entity =>
    ({ get }) => {
      let rows = get(tableRowsAtomFamily(entity));

      if (entity === "ride") {
        const nearbyOtherRidesIdsBatch = get(nearbyOtherRidesIdsBatchAtom);
        const nearByFilterIsActive = get(nearByFilterAtomFamily(entity));
        const filteringRideId = get(nearbyFilteringRideIdAtom);

        if (nearByFilterIsActive) {
          const ridesIdsWithNearby = getRidesIdsWithNearby(
            nearbyOtherRidesIdsBatch,
          );
          const nearByRidesIdsValues = getNearbyRidesIdsValues(
            nearbyOtherRidesIdsBatch,
          );

          const ridesIdsWithNearbySet = new Set(ridesIdsWithNearby);
          const nearByRidesIdsValuesSet = new Set(nearByRidesIdsValues);
          rows =
            rows?.filter(
              x =>
                ridesIdsWithNearbySet.has(x.id) ||
                nearByRidesIdsValuesSet.has(x.id),
            ) ?? [];
        }

        if (filteringRideId) {
          const otherNearbyRides =
            nearbyOtherRidesIdsBatch.data[filteringRideId] ?? [];
          const nearbyIds = [...otherNearbyRides, filteringRideId];

          const nearbyIdsSet = new Set(nearbyIds);
          return rows?.filter(x => nearbyIdsSet.has(x.id)) ?? [];
        }
      }

      if (entity === "rideMap") {
        const showOnlyNewIncidents = get(
          showOnlyRidesWithNewIncidentsAtomFamily(entity),
        );

        if (showOnlyNewIncidents) {
          rows = rows?.filter(r => {
            const n = (r as IRideTableDto)[ComputedDataKey]?.[
              RideComputedDataKeys.newIncidentsCount
            ];
            return isValidPositiveNumber(n);
          });
        }

        const hiddenDelayStatuses = get(
          liveMapHiddenDelayStatusesAtomFamily(entity),
        );
        rows = filterLiveMapRowsByHiddenDelayStatus(
          rows as IRideTableDto[] | undefined,
          hiddenDelayStatuses,
        );

        const rideFilter = get(liveMapRideFilterAtomFamily(entity));
        rows = filterLiveMapRowsByRideFilter({
          rides: rows as IRideTableDto[] | undefined,
          filter: rideFilter,
        });
      }

      return rows || [];
    },
});

/** Total count of incidents in New status from the latest live-map search toolbar stats atom. */
export const newIncidentsNewStatusTotalCountRideMapSF = selectorFamily<
  number,
  RydeUiUnit
>({
  key: "newIncidentsNewStatusTotalCountRideMapSF",
  get:
    entity =>
    ({ get }) => {
      if (entity !== "rideMap") {
        return 0;
      }

      return get(liveMapGridToolbarStatsAtomFamily(entity)).newIncidentsTotal;
    },
});

export const isHighlightedAfterEditSF = selectorFamily<
  boolean,
  [entity: RydeUiUnit, id: number]
>({
  key: "isHighlightedAfterEditSF",
  get:
    ([entity, id]) =>
    ({ get }) => {
      const highlightedIds = get(highlightedAfterEditAtomFamily(entity));
      const highlightedIdsSet = new Set(highlightedIds);
      return highlightedIdsSet.has(id);
    },
});

export const selectedTableDataCountSF = selectorFamily<number, RydeUiUnit>({
  key: "selectedTableDataCountSF",
  get:
    entity =>
    ({ get }) => {
      const tableRowIds = get(selectedTableRowsIdsAtomFamily(entity));
      return tableRowIds?.length || 0;
    },
});

const getLoadedTableRowsForSelection = ({
  get,
  entity,
}: {
  get: <T>(recoilValue: RecoilValue<T>) => T;
  entity: RydeUiUnit;
}): IExtendableDto[] => {
  if (isEntityServerSidePaginated(entity)) {
    return flattenGroupedTableRows(get(tableFilteredGroupedRowsMapSF(entity)));
  }

  return get(tableRowsAtomFamily(entity)) ?? [];
};

export const selectedTableRowsBulkUpdateDefaultValues = boundedSelectorFamily<
  IExtendableDto | null,
  RydeUiUnit
>({
  key: "selectedTableRowsBulkUpdateDefaultValues",
  get:
    entity =>
    ({ get }) => {
      const tableRowIds = get(selectedTableRowsIdsAtomFamily(entity));
      const tableRows = getLoadedTableRowsForSelection({ get, entity });

      const firstSelectedRow = tableRows.find(
        ({ id }) => id === tableRowIds.at(0),
      );

      return firstSelectedRow ?? null;
    },
});

export const selectedTableRowsValues = boundedSelectorFamily<
  IExtendableDto[],
  RydeUiUnit
>({
  key: "selectedTableRowsValues",
  get:
    entity =>
    ({ get }) => {
      const tableRowIds = get(selectedTableRowsIdsAtomFamily(entity));
      const tableRows = getLoadedTableRowsForSelection({ get, entity });

      return resolveSelectedTableRowValues({
        selectedIds: tableRowIds,
        rows: tableRows,
      });
    },
});

export const notificationsDrawerListSelector = boundedSelector({
  key: "notificationsDrawerListSelector",
  get: ({ get }) => {
    const notificationsImportant = get(notificationsImportantAtom);
    const notificationsTableDtos = get(tableRowsAtomFamily("notification"));
    const notificationsDrawerList =
      (notificationsTableDtos as INotificationDto[] | undefined)?.filter(
        ({ id }) => {
          return notificationsImportant.ids.has(id);
        },
      ) ?? [];
    return notificationsDrawerList;
  },
});
